import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppConfigService } from 'app/core/config/app-config.service';

/** Recipient groups offered in the chat drawer. `students` is served by ChatService. */
export type DirectChatCategoryId = 'students' | 'supervisors' | 'translators' | 'courseadmins' | 'sysadmins';

export interface DirectChatCategory {
  id: DirectChatCategoryId;
  unread: number;
}

export interface DirectChatRecipient {
  account_id: number;
  name: string;
  email: string | null;
  role_id: number;
  company_name: string | null;
  avatar: string | null;
  unread: number;
}

export interface DirectChatMessage {
  id: number;
  sender_account_id: number;
  message: string;
  created: string;
  read_at: string | null;
  is_mine: boolean;
}

export interface DirectChatPage<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
}

/** Private 1:1 staff conversations (/v1/direct-chat). Access is decided by the API. */
@Injectable({ providedIn: 'root' })
export class DirectChatService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(AppConfigService);

  private get url(): string {
    return `${this.config.get('serverUrl')}/v1/direct-chat`;
  }

  getCategories(): Observable<DirectChatCategory[]> {
    return this.http.get<{ data: DirectChatCategory[] }>(`${this.url}/categories`).pipe(map((r) => r.data || []));
  }

  getRecipients(category: DirectChatCategoryId, search = '', page = 1, limit = 50): Observable<DirectChatPage<DirectChatRecipient>> {
    const params = new HttpParams({ fromObject: { category, search, page: String(page), limit: String(limit) } });
    return this.http.get<DirectChatPage<DirectChatRecipient>>(`${this.url}/recipients`, { params });
  }

  getMessages(accountId: number, page = 1, perPage = 20): Observable<DirectChatPage<DirectChatMessage>> {
    const params = new HttpParams({ fromObject: { page: String(page), per_page: String(perPage) } });
    return this.http.get<DirectChatPage<DirectChatMessage>>(`${this.url}/with/${accountId}/messages`, { params });
  }

  sendMessage(accountId: number, message: string): Observable<DirectChatMessage> {
    return this.http
      .post<{ data: DirectChatMessage }>(`${this.url}/with/${accountId}/messages`, { message })
      .pipe(map((r) => r.data));
  }

  markRead(accountId: number): Observable<unknown> {
    return this.http.post(`${this.url}/with/${accountId}/read`, {});
  }

  getUnreadTotal(): Observable<number> {
    return this.http.get<{ total: number }>(`${this.url}/unread`).pipe(map((r) => Number(r.total || 0)));
  }
}
